import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import '../styles/PayslipHistory.css';
import { FiSearch, FiDownload, FiEye, FiX, FiPrinter, FiCalendar } from 'react-icons/fi';
import Cookies from 'js-cookie';
import CommonLoader from '../../../components/common/CommonLoader';
import { GRAPHQL_HTTP_URL, API_ORIGIN } from '../../../config/api';
import { workforceRoleLabel } from '../../WorkForce/utils/workforcePeople';

const GQL = GRAPHQL_HTTP_URL;
const API_BASE = API_ORIGIN;

const PAGE_SIZE = 6;


const fmt = (n) => `$${Number(n ?? 0).toFixed(2)}`;

/** Map backend PayslipStatus enum to UI { cls, label } */
function mapStatus(backendStatus) {
    switch (backendStatus?.toLowerCase()) {
        case 'sent':      return { cls: 'paid',      label: 'Paid' };
        case 'locked':    return { cls: 'locked',    label: 'ABA Queued' };
        case 'failed':    return { cls: 'cancelled', label: 'Failed' };
        case 'generated': return { cls: 'generated', label: 'Generated' };
        default:          return { cls: 'generated', label: 'Generated' };
    }
}

/** Parse a date-only "YYYY-MM-DD" string as local date (avoids UTC midnight → day-before shift in AEST/AEST+) */
function parseLocalDate(dateStr) {
    if (!dateStr) return new Date(NaN);
    const s = String(dateStr).split('T')[0]; // strip time if present
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d); // local midnight — no UTC shift
}

/** Show payslip period as month, e.g. "Mar 2026" */
function fmtPeriodMonth(startDateStr) {
    if (!startDateStr) return '';
    const d = parseLocalDate(startDateStr);
    return d.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
}

function fmtDate(dateStr) {
    if (!dateStr) return '—';
    const d = parseLocalDate(dateStr);
    return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Capitalise first letter of salary term, e.g. "weekly" → "Weekly" */
function fmtSalaryTerm(term) {
    if (!term) return '';
    return term.charAt(0).toUpperCase() + term.slice(1).toLowerCase();
}

/** Format a date value to YYYY-MM-DD for GraphQL variables */
function toYMD(dateVal) {
    if (!dateVal) return '';
    // If it already looks like YYYY-MM-DD, return as-is (no UTC conversion needed)
    const s = String(dateVal).split('T')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const d = parseLocalDate(s);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/** Derive avatar initials from full name */
function initials(name) {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : parts[0].slice(0, 2).toUpperCase();
}

export default function PayslipHistory() {
    const [historyData, setHistoryData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [periodFilter, setPeriodFilter] = useState('last30');
    const [page, setPage] = useState(1);
    const [selectedPayslip, setSelectedPayslip] = useState(null);
    const [downloadingId, setDownloadingId] = useState(null);
    const [downloadingAll, setDownloadingAll] = useState(false);
    const token = Cookies.get('accessToken');
    const companyId = Cookies.get('companyId');

    /** Returns today as a local "YYYY-MM-DD" string — no UTC shift */
    const toLocalYMD = useCallback((d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }, []);

    /** Returns start/end as "YYYY-MM-DD" strings for pure string comparison */
    const getDateRange = useCallback((range) => {
        const now = new Date();
        const todayStr = toLocalYMD(now);
        switch (range) {
            case 'last7': {
                const s = new Date(now); s.setDate(now.getDate() - 6);
                return { start: toLocalYMD(s), end: todayStr };
            }
            case 'last30': {
                const s = new Date(now); s.setDate(now.getDate() - 29);
                return { start: toLocalYMD(s), end: todayStr };
            }
            case 'thisMonth': {
                const s = new Date(now.getFullYear(), now.getMonth(), 1);
                const e = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                return { start: toLocalYMD(s), end: toLocalYMD(e) };
            }
            case 'lastMonth': {
                const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                const e = new Date(now.getFullYear(), now.getMonth(), 0);
                return { start: toLocalYMD(s), end: toLocalYMD(e) };
            }
            case 'all':
            default:
                return null;
        }
    }, [toLocalYMD]);

    const fetchPayslips = useCallback(async ({ silent = false } = {}) => {
        if (!token || !companyId) return [];
        if (!silent) setLoading(true);
        try {
            const range = getDateRange(periodFilter);
            const res = await fetch(GQL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    query: `query Payslips($startDate: String, $endDate: String) {
                        payslips(startDate: $startDate, endDate: $endDate) {
                            id
                            userId
                            employeeName
                            employeeCode
                            payPeriodStart
                            payPeriodEnd
                            grossPay
                            taxAmount
                            superannuationAmount
                            netPay
                            status
                            pdfUrl
                        }
                    }`,
                    variables: {
                        startDate: range?.start || null,
                        endDate: range?.end || null,
                    },
                }),
            });
            const json = await res.json();
            if (!res.ok || json?.errors?.length) {
                throw new Error(json?.errors?.[0]?.message || `HTTP ${res.status}`);
            }

            const payslips = json?.data?.payslips ?? [];

            const mapped = payslips.map(p => {
                const netPay = Number.isFinite(Number(p.netPay))
                    ? Number(p.netPay)
                    : (Number(p.grossPay) - Number(p.taxAmount));
                return {
                    id: p.id,
                    empId: p.userId,
                    name: p.employeeName,
                    avatar: initials(p.employeeName),
                    period: fmtPeriodMonth(p.payPeriodStart),
                    gross: Number(p.grossPay) || 0,
                    tax: Number(p.taxAmount) || 0,
                    superAmt: Number(p.superannuationAmount) || 0,
                    net: netPay,
                    status: mapStatus(p.status),
                    pdfUrl: p.pdfUrl || null,
                    raw: p,
                };
            });

            setHistoryData(mapped);
            return mapped;
        } catch (err) {
            console.error('Error fetching payslips:', err);
            return [];
        } finally {
            if (!silent) setLoading(false);
        }
    }, [token, companyId, periodFilter, getDateRange]);

    useEffect(() => {
        fetchPayslips();
    }, [fetchPayslips]);

    const filtered = historyData.filter(h => {
        const matchSearch = !search ||
            h.name.toLowerCase().includes(search.toLowerCase()) ||
            h.id.toLowerCase().includes(search.toLowerCase()) ||
            h.empId.toLowerCase().includes(search.toLowerCase());
        return matchSearch;
    });

    const totalPages = Math.max(1, Math.max(1, Math.ceil(filtered.length / PAGE_SIZE)));
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const handleSearch = val => { setSearch(val); setPage(1); };
    const handlePeriod = (val) => { setPeriodFilter(val); setPage(1); };

    // ── Download helpers ────────────────────────────────────────────────────────

    /** Trigger a browser file download from a resolved URL */
    const triggerDownload = async (resolvedUrl, fileName) => {
        const fileRes = await fetch(resolvedUrl, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!fileRes.ok) {
            let detail = `HTTP ${fileRes.status}`;
            try {
                const errBody = await fileRes.json();
                if (errBody?.message) detail = errBody.message;
            } catch { /* non-JSON body, keep status code */ }
            throw new Error(detail);
        }
        const blob = await fileRes.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
    };

    /**
     * Download flow:
     *   1. If payslip has no DB record yet → call generatePayslip mutation first.
     *   2. Use backend proxy /payroll/payslip-download/:id to fetch the PDF from S3.
     *      (Avoids CORS issues with direct S3 fetch from the browser.)
     */
    const downloadPayslip = async (payslip, label) => {
        const safeLabel = (label || `${payslip.employeeName}_${fmtPeriodMonth(payslip.payPeriodStart)}`)
            .replace(/[^a-zA-Z0-9_\- ]/g, '_');
        const fileName = `${safeLabel}.pdf`;

        let payslipId = payslip.id;

        // Step 1 — if no payslip record yet, generate it first to get the ID.
        if (!payslipId) {
            const startDate = toYMD(payslip.payPeriodStart);
            const endDate = toYMD(payslip.payPeriodEnd);
            const gqlRes = await fetch(GQL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    query: `mutation GeneratePayslip($empId: String!, $start: String!, $end: String!) {
                        generatePayslip(employeeId: $empId, startDate: $start, endDate: $end) {
                            id
                        }
                    }`,
                    variables: { empId: payslip.userId, start: startDate, end: endDate },
                }),
            });
            const gqlJson = await gqlRes.json();
            if (!gqlRes.ok || gqlJson?.errors?.length) {
                throw new Error(gqlJson?.errors?.[0]?.message || `HTTP ${gqlRes.status}`);
            }
            payslipId = gqlJson?.data?.generatePayslip?.id;
            if (!payslipId) throw new Error('Could not generate payslip');
        }

        // Step 2 — download via backend proxy (backend fetches from S3, no CORS issue).
        const downloadUrl = `${API_BASE}/payroll/payslip-download/${payslipId}`;
        await triggerDownload(downloadUrl, fileName);
    };

    /** Download a single payslip row with per-row loading indicator. */
    const handleDownload = async (h) => {
        setDownloadingId(h.id);
        try {
            await downloadPayslip(h.raw, `${h.name}_${h.period}`);
        } catch (err) {
            console.error('Download failed:', err);
            toast.error(`Download failed: ${err.message}`);
        } finally {
            setDownloadingId(null);
        }
    };

    /** Download all payslips in the current filtered list sequentially. */
    const handleDownloadAll = async () => {
        if (filtered.length === 0) return;
        setDownloadingAll(true);
        let failCount = 0;
        for (const h of filtered) {
            try {
                // eslint-disable-next-line no-await-in-loop
                await downloadPayslip(h.raw, `${h.name}_${h.period}`);
                // Small stagger so browsers don't block rapid sequential downloads.
                // eslint-disable-next-line no-await-in-loop
                await new Promise(r => setTimeout(r, 400));
            } catch (err) {
                console.error(`Download failed for ${h.name}:`, err);
                failCount += 1;
            }
        }
        setDownloadingAll(false);
        if (failCount > 0) {
            toast.error(`${failCount} payslip(s) could not be downloaded. Please try individually.`);
        }
    };

    // ── View modal helpers ──────────────────────────────────────────────────────

    const maskAccount = (acc) => {
        if (!acc || acc.length <= 4) return acc || '—';
        return acc.slice(0, 2) + '*'.repeat(acc.length - 4) + acc.slice(-2);
    };

    const handleViewDownload = async () => {
        if (!selectedPayslip) return;
        setDownloadingId(selectedPayslip.id);
        try {
            await downloadPayslip(selectedPayslip, `${selectedPayslip.employeeName}_${fmtPeriodMonth(selectedPayslip.payPeriodStart)}`);
        } catch (err) {
            toast.error(`Download failed: ${err.message}`);
        } finally {
            setDownloadingId(null);
        }
    };

    return (
        <>
        <div className="ph-page">

            {/* Header */}
            <div className="ph-header">
                <div>
                    <h2 className="ph-title">Payslip History</h2>
                    <p className="ph-subtitle">View and download past payslips for all employees</p>
                </div>
            </div>

            {/* Filters */}
            <div className="ph-filters">
                <div className="ph-search">
                    <FiSearch size={14} />
                    <input
                        placeholder="Search by name, ID or payslip #..."
                        value={search}
                        onChange={e => handleSearch(e.target.value)}
                    />
                </div>
                <div className="ph-filter-right">
                    <div className="ph-period-filter">
                        <FiCalendar size={13} />
                        <select value={periodFilter} onChange={e => handlePeriod(e.target.value)}>
                            <option value="last30">Last 30 days</option>
                            <option value="last7">Last 7 days</option>
                            <option value="thisMonth">This Month</option>
                            <option value="lastMonth">Last Month</option>
                            <option value="all">All</option>
                        </select>
                    </div>
                    <button
                        className="ph-download-all-btn"
                        onClick={handleDownloadAll}
                        disabled={downloadingAll || filtered.length === 0}
                    >
                        <FiDownload size={13} />
                        {downloadingAll ? 'Downloading…' : 'Download All'}
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="ph-section">
                <div className="ph-section-header">
                    <span className="ph-section-title">Payslip Records</span>
                    <span className="ph-count-badge">{filtered.length} records</span>
                </div>

                <div className="ph-table-wrap">
                    <table className="ph-table">
                        <thead>
                            <tr>
                                <th>Employee Name</th>
                                <th>Month</th>
                                <th>Gross Pay</th>
                                <th>Deductions</th>
                                <th>Net Pay</th>
                                <th>Status</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={7}>
                                        <CommonLoader message="Loading payslips…" />
                                    </td>
                                </tr>
                            ) : paginated.length === 0 ? (
                                <tr>
                                    <td colSpan={7} style={{ textAlign: 'center', padding: 32, color: '#94a3b8' }}>
                                        No payslips found.
                                    </td>
                                </tr>
                            ) : paginated.map(h => (
                                <tr key={h.id}>
                                    <td>
                                        <div className="ph-emp-name">{h.name}</div>
                                    </td>
                                    <td>{h.period}</td>
                                    <td>{fmt(h.gross)}</td>
                                    <td>{fmt(h.tax)}</td>
                                    <td className="ph-net-pay">{fmt(h.net)}</td>
                                    <td>
                                        <span className={`ph-status ${h.status.cls}`}>
                                            {h.status.label}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="ph-action-cell">
                                            <button
                                                className="ph-view-btn"
                                                onClick={async () => {
                                                    try {
                                                        setLoading(true);
                                                        const r = await fetch(GQL, {
                                                            method: 'POST',
                                                            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                                            body: JSON.stringify({
                                                                query: `query Payslip($id: String!) {
                                                                    payslip(id: $id) {
                                                                        id
                                                                        userId
                                                                        employeeCode
                                                                        employeeName
                                                                        payPeriodStart
                                                                        payPeriodEnd
                                                                        payDate
                                                                        salaryTerm
                                                                        jobTitle
                                                                        bsb
                                                                        accountNumber
                                                                        accountName
                                                                        regularHours
                                                                        overtimeHours
                                                                        saturdayHours
                                                                        sundayHours
                                                                        totalHoursWorked
                                                                        perHourRate
                                                                        overtimeRate
                                                                        regularPay
                                                                        grossPay
                                                                        overtimePay
                                                                        saturdayPay
                                                                        sundayPay
                                                                        bonus
                                                                        allowance
                                                                        commission
                                                                        taxAmount
                                                                        taxRate
                                                                        superannuationAmount
                                                                        superannuationRate
                                                                        netPay
                                                                        annualLeaveAccrued
                                                                        sickLeaveAccrued
                                                                        fundName
                                                                        memberNumber
                                                                        status
                                                                        pdfUrl
                                                                        paymentMethod
                                                                        isTaxCalculated
                                                                        user {
                                                                            email
                                                                            contact
                                                                            roles
                                                                            company {
                                                                                companyName
                                                                                abn
                                                                            }
                                                                        }
                                                                    }
                                                                }`,
                                                                variables: { id: h.id },
                                                            }),
                                                        });
                                                        const j = await r.json();
                                                        if (!r.ok || j?.errors?.length) {
                                                            throw new Error(j?.errors?.[0]?.message || `HTTP ${r.status}`);
                                                        }
                                                        setSelectedPayslip(j?.data?.payslip || null);
                                                    } catch (e) {
                                                        console.error('Failed to load payslip details', e);
                                                        toast.error(`Failed to load payslip: ${e.message}`);
                                                    } finally {
                                                        setLoading(false);
                                                    }
                                                }}
                                            >
                                                <FiEye size={12} /> View
                                            </button>
                                            <button
                                                className="ph-download-btn"
                                                onClick={() => handleDownload(h)}
                                                disabled={downloadingId === h.id || downloadingAll}
                                            >
                                                <FiDownload size={12} />
                                                {downloadingId === h.id ? 'Downloading…' : 'Download'}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="ph-pagination">
                    <span>
                        {filtered.length === 0
                            ? '0 records'
                            : `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, filtered.length)} of ${filtered.length}`}
                    </span>
                    <div className="ph-page-btns">
                        <button className="ph-page-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                            <button key={n} className={`ph-page-btn ${n === page ? 'active' : ''}`} onClick={() => setPage(n)}>{n}</button>
                        ))}
                        <button className="ph-page-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>›</button>
                    </div>
                </div>
            </div>

        </div>

        {/* Payslip View — same layout as the PDF */}
        {selectedPayslip && (
            <div className="ph-detail-overlay" onClick={() => setSelectedPayslip(null)}>
                <div className="ph-detail-modal" onClick={e => e.stopPropagation()}>

                    {/* Action bar above the card */}
                    <div className="ph-detail-top">
                        <span style={{ fontSize: 12, color: '#94a3b8' }}>
                            {selectedPayslip.user?.company?.companyName || ''}
                        </span>
                        <div className="ph-detail-actions">
                            <button
                                className="ph-plain-btn"
                                onClick={handleViewDownload}
                                disabled={downloadingId === selectedPayslip.id}
                            >
                                <FiDownload size={12} />
                                {downloadingId === selectedPayslip.id ? 'Downloading…' : 'Download PDF'}
                            </button>
                            <button className="ph-print-btn" onClick={() => window.print()}>
                                <FiPrinter size={12} /> Print
                            </button>
                            <button
                                className="ph-plain-btn"
                                style={{ border: 'none', background: 'transparent', color: '#64748b' }}
                                onClick={() => setSelectedPayslip(null)}
                            >
                                <FiX size={16} />
                            </button>
                        </div>
                    </div>

                    {/* Payslip card */}
                    <div className="ph-slip-card">

                        {/* 1. Header — "PAYSLIP" left, pay period + pay date right */}
                        <div className="ph-slip-header">
                            <h3>PAYSLIP</h3>
                            <div className="ph-slip-meta">
                                <div>
                                    Pay Period: {fmtDate(selectedPayslip.payPeriodStart)} – {fmtDate(selectedPayslip.payPeriodEnd)}
                                    {selectedPayslip.salaryTerm ? ` · ${fmtSalaryTerm(selectedPayslip.salaryTerm)}` : ''}
                                </div>
                                <div>Pay Date: {fmtDate(selectedPayslip.payDate)}</div>
                            </div>
                        </div>

                        {/* 2. Info grid — Employer Details | Employee Details */}
                        <div className="ph-info-grid">
                            <div className="ph-info-box">
                                <h4>Employer Details</h4>
                                <p><strong>Name:</strong> {selectedPayslip.user?.company?.companyName || '—'}</p>
                                <p><strong>ABN:</strong> {selectedPayslip.user?.company?.abn || '—'}</p>
                            </div>
                            <div className="ph-info-box">
                                <h4>Employee Details</h4>
                                <p><strong>Name:</strong> {selectedPayslip.employeeName || '—'}</p>
                                <p><strong>Employee ID:</strong> {selectedPayslip.employeeCode || '—'}</p>
                                <p><strong>Email:</strong> {selectedPayslip.user?.email || '—'}</p>
                                <p><strong>Contact:</strong> {selectedPayslip.user?.contact || '—'}</p>
                                <p><strong>Role:</strong> {workforceRoleLabel(selectedPayslip.user?.roles) || '—'}</p>
                                <p><strong>Level:</strong> {selectedPayslip.jobTitle || '—'}</p>
                            </div>
                        </div>

                        {/* 3. Work Summary */}
                        <div className="ph-block-title">Work Summary</div>
                        <div className="ph-summary-grid">
                            <div>
                                <span>Regular Hrs</span>
                                <b>{Number(selectedPayslip.regularHours || 0).toFixed(2)} hrs</b>
                            </div>
                            <div>
                                <span>Overtime Hrs</span>
                                <b>{Number(selectedPayslip.overtimeHours || 0).toFixed(2)} hrs</b>
                            </div>
                            {Number(selectedPayslip.saturdayHours || 0) > 0 && (
                                <div>
                                    <span>Saturday Hrs</span>
                                    <b>{Number(selectedPayslip.saturdayHours).toFixed(2)} hrs</b>
                                </div>
                            )}
                            {Number(selectedPayslip.sundayHours || 0) > 0 && (
                                <div>
                                    <span>Sunday Hrs</span>
                                    <b>{Number(selectedPayslip.sundayHours).toFixed(2)} hrs</b>
                                </div>
                            )}
                            <div>
                                <span>Total Hrs</span>
                                <b>{Number(selectedPayslip.totalHoursWorked || 0).toFixed(2)} hrs</b>
                            </div>
                        </div>

                        {/* 4. Salary / Wages */}
                        <div className="ph-block-title">Salary / Wages</div>
                        <table className="ph-detail-table">
                            <thead>
                                <tr>
                                    <th>Salary / Wages</th>
                                    <th style={{ textAlign: 'right' }}>Rate</th>
                                    <th style={{ textAlign: 'right' }}>Hours</th>
                                    <th style={{ textAlign: 'right' }}>This Pay</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Ordinary Hours</td>
                                    <td style={{ textAlign: 'right' }}>{fmt(selectedPayslip.perHourRate)}/hr</td>
                                    <td style={{ textAlign: 'right' }}>{Number(selectedPayslip.regularHours || 0).toFixed(2)}</td>
                                    <td style={{ textAlign: 'right' }}>{fmt(selectedPayslip.regularPay ?? (Number(selectedPayslip.regularHours || 0) * Number(selectedPayslip.perHourRate || 0)))}</td>
                                </tr>
                                {Number(selectedPayslip.overtimeHours || 0) > 0 && (
                                    <tr>
                                        <td>Overtime</td>
                                        <td style={{ textAlign: 'right' }}>{fmt(selectedPayslip.overtimeRate)}/hr</td>
                                        <td style={{ textAlign: 'right' }}>{Number(selectedPayslip.overtimeHours).toFixed(2)}</td>
                                        <td style={{ textAlign: 'right' }}>{fmt(selectedPayslip.overtimePay)}</td>
                                    </tr>
                                )}
                                {Number(selectedPayslip.saturdayHours || 0) > 0 && (
                                    <tr>
                                        <td>Saturday Penalty</td>
                                        <td style={{ textAlign: 'right' }}>{fmt(Number(selectedPayslip.perHourRate || 0) * 1.5)}/hr</td>
                                        <td style={{ textAlign: 'right' }}>{Number(selectedPayslip.saturdayHours).toFixed(2)}</td>
                                        <td style={{ textAlign: 'right' }}>{fmt(selectedPayslip.saturdayPay)}</td>
                                    </tr>
                                )}
                                {Number(selectedPayslip.sundayHours || 0) > 0 && (
                                    <tr>
                                        <td>Sunday Penalty</td>
                                        <td style={{ textAlign: 'right' }}>{fmt(Number(selectedPayslip.perHourRate || 0) * 2)}/hr</td>
                                        <td style={{ textAlign: 'right' }}>{Number(selectedPayslip.sundayHours).toFixed(2)}</td>
                                        <td style={{ textAlign: 'right' }}>{fmt(selectedPayslip.sundayPay)}</td>
                                    </tr>
                                )}
                                {Number(selectedPayslip.bonus || 0) > 0 && (
                                    <tr>
                                        <td>Bonus</td>
                                        <td></td>
                                        <td></td>
                                        <td style={{ textAlign: 'right' }}>{fmt(selectedPayslip.bonus)}</td>
                                    </tr>
                                )}
                                {Number(selectedPayslip.allowance || 0) > 0 && (
                                    <tr>
                                        <td>Allowance</td>
                                        <td></td>
                                        <td></td>
                                        <td style={{ textAlign: 'right' }}>{fmt(selectedPayslip.allowance)}</td>
                                    </tr>
                                )}
                                {Number(selectedPayslip.commission || 0) > 0 && (
                                    <tr>
                                        <td>Commission</td>
                                        <td></td>
                                        <td></td>
                                        <td style={{ textAlign: 'right' }}>{fmt(selectedPayslip.commission)}</td>
                                    </tr>
                                )}
                                <tr className="ph-total-row">
                                    <td>Total</td>
                                    <td></td>
                                    <td></td>
                                    <td style={{ textAlign: 'right' }}>{fmt(selectedPayslip.grossPay)}</td>
                                </tr>
                            </tbody>
                        </table>

                        {/* 5. Employer Contribution */}
                        <div className="ph-block-title">Employer Contribution</div>
                        <table className="ph-detail-table">
                            <thead>
                                <tr>
                                    <th>Employer Contribution</th>
                                    <th style={{ textAlign: 'right' }}>Rate</th>
                                    <th style={{ textAlign: 'right' }}>OTE Base</th>
                                    <th style={{ textAlign: 'right' }}>This Pay</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>
                                        Superannuation
                                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                                            On ordinary time earnings (excl. penalty rates)
                                        </div>
                                    </td>
                                    <td style={{ textAlign: 'right' }}>{Number(selectedPayslip.superannuationRate || 0).toFixed(2)}%</td>
                                    <td style={{ textAlign: 'right' }}>
                                        {fmt((Number(selectedPayslip.regularHours || 0) + Number(selectedPayslip.overtimeHours || 0)) * Number(selectedPayslip.perHourRate || 0))}
                                    </td>
                                    <td style={{ textAlign: 'right' }}>{fmt(selectedPayslip.superannuationAmount)}</td>
                                </tr>
                            </tbody>
                        </table>

                        {/* 6. Tax */}
                        <div className="ph-block-title">Tax</div>
                        <table className="ph-detail-table">
                            <thead>
                                <tr>
                                    <th>Tax</th>
                                    <th style={{ textAlign: 'right' }}>This Pay</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>PAYG Withholding</td>
                                    <td style={{ textAlign: 'right' }}>{fmt(selectedPayslip.taxAmount)}</td>
                                </tr>
                            </tbody>
                        </table>

                        {/* 7. Leave Accrual */}
                        {(Number(selectedPayslip.annualLeaveAccrued || 0) > 0 || Number(selectedPayslip.sickLeaveAccrued || 0) > 0) && (
                            <>
                                <div className="ph-block-title">Leave Accrual This Period</div>
                                <table className="ph-detail-table">
                                    <thead>
                                        <tr>
                                            <th>Leave Type</th>
                                            <th style={{ textAlign: 'right' }}>Accrued (hrs)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Number(selectedPayslip.annualLeaveAccrued || 0) > 0 && (
                                            <tr>
                                                <td>Annual Leave</td>
                                                <td style={{ textAlign: 'right' }}>{Number(selectedPayslip.annualLeaveAccrued).toFixed(2)}</td>
                                            </tr>
                                        )}
                                        {Number(selectedPayslip.sickLeaveAccrued || 0) > 0 && (
                                            <tr>
                                                <td>Personal / Sick Leave</td>
                                                <td style={{ textAlign: 'right' }}>{Number(selectedPayslip.sickLeaveAccrued).toFixed(2)}</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </>
                        )}

                        {/* 8. Payment Details */}
                        <div className="ph-block-title">Payment Details</div>
                        <table className="ph-detail-table">
                            <thead>
                                <tr>
                                    <th>Payment Details</th>
                                    <th style={{ textAlign: 'right' }}>Reference</th>
                                    <th style={{ textAlign: 'right' }}>Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>{selectedPayslip.paymentMethod === 'CASH'
                                        ? (selectedPayslip.isTaxCalculated === false ? 'Cash (Tax Excluded)' : 'Cash')
                                        : ([selectedPayslip.bsb, maskAccount(selectedPayslip.accountNumber), selectedPayslip.accountName].filter(Boolean).join(' / ') || '—')
                                    }</td>
                                    <td style={{ textAlign: 'right' }}>{selectedPayslip.employeeName || '—'}</td>
                                    <td style={{ textAlign: 'right' }}>{fmt(selectedPayslip.netPay)}</td>
                                </tr>
                            </tbody>
                        </table>

                        {/* 9. Net Pay box */}
                        <div className="ph-net-box">
                            <div>
                                <span style={{ display: 'block' }}>Net Amount</span>
                                <span style={{ fontSize: 9, color: '#64748b' }}>
                                    Net Pay {fmt(selectedPayslip.netPay)} + Super {fmt(selectedPayslip.superannuationAmount)}
                                </span>
                            </div>
                            <strong>{fmt(Number(selectedPayslip.netPay || 0) + Number(selectedPayslip.superannuationAmount || 0))}</strong>
                        </div>

                        {/* 10. Footnote */}
                        <p className="ph-slip-footnote">
                            This is a payslip generated by the payroll system and may not contain all statutory notes.
                        </p>

                    </div>
                </div>
            </div>
        )}
        </>
    );
}
